import * as React from 'react'

import {
  Box,
  Button,
  NoticeBox,
  type SelectOption,
  Text,
  Title,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import SelectField from 'modules/core/components/SelectField'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { formatBackendDateToDayMonthYear } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import policyTimelines from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'
import type PolicyTimeline from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'
import type { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

import List from './List'
import UpsertAssignment from './UpsertAssignment'

interface Props {
  employeeId: string
  employeeCanEditPolicyAssignments: boolean
  selectedTimelineItem: PolicyTimelineItem | null | undefined
  setSelectedTimelineItem: (item: PolicyTimelineItem) => void
}

const LearnMoreLink = () => (
  <Box paddingLeft="s32" paddingTop="s12">
    <HelpCenterLink name="timeoff_policy_timeline">
      <Text.Normal underlined weight="semibold">
        {i18n.t('time_off.policy_timeline.learn_more')}
      </Text.Normal>
    </HelpCenterLink>
  </Box>
)

function PolicyAssignments({
  employeeId,
  employeeCanEditPolicyAssignments,
  selectedTimelineItem,
  setSelectedTimelineItem,
}: Props) {
  const [showSidePanel, toggleSidePanel] = useToggle(false)
  const [showUpsertForm, toggleUpsertForm] = useToggle(false)

  const [timeline, setTimeline] = React.useState<PolicyTimeline | null | undefined>()

  useUnsafeEffect(() => {
    const firstTimeline = policyTimelines.at(0)
    if (firstTimeline && 'current' in firstTimeline) {
      setTimeline(firstTimeline)
      setSelectedTimelineItem(firstTimeline.current)
    }
  }, [employeeId, timeline, setSelectedTimelineItem])

  let policyOptions: SelectOption[] = []

  if (timeline) {
    policyOptions = timeline.items.map((item) => {
      return {
        label: `${i18n.t('contract_versions.header.effective_on')} ${item.start_at}`,
        value: item.policy_assignment_id ? item.policy_assignment_id : '0', // reason to be 0 in line 103
      }
    })
    policyOptions.unshift({
      disabled: true,
      label: i18n.t('time_off.policy_timeline.descriptions.current_assignment'),
      value: -1, // We just use a negative value since this option can't be selected and values should be unique since we use them as keys
    })
    policyOptions.splice(2, 0, {
      disabled: true,
      label: i18n.t('time_off.policy_timeline.descriptions.previous_assignments'),
      value: -2, // We just use a negative value since this option can't be selected and values should be unique since we use them as keys
    })
  }

  const fetchTimeline = async () => {
    await policyTimelines.fetch({
      data: {
        employee_id: employeeId,
      },
    })
    const firstTimeline = policyTimelines.at(0)
    if (firstTimeline && 'current' in firstTimeline) {
      setTimeline(firstTimeline)
      setSelectedTimelineItem(firstTimeline.current)
    }
  }

  const onSelectTimelineItem = (item: PolicyTimelineItem) => {
    if (item.policy_assignment_id !== selectedTimelineItem?.policy_assignment_id) {
      setSelectedTimelineItem(item)
    }
    toggleUpsertForm(false)()
  }

  const onSelectAssignment = (value: string) => {
    const item = timeline?.items.find((it) => {
      // This is the case for the "Default" assignment (which is virtual and has no id)
      if (value === '0') return it.policy_assignment_id === null
      return it.policy_assignment_id === value
    })
    if (item) setSelectedTimelineItem(item)
  }

  const selectedAssignmentTitle = () => {
    if (timeline?.current?.policy_assignment_id === selectedTimelineItem?.policy_assignment_id) {
      return i18n.t('time_off.policy_timeline.descriptions.current_assignment')
    } else if (selectedTimelineItem?.isFuture) {
      return i18n.t('time_off.policy_timeline.descriptions.future_assignments')
    } else {
      return i18n.t('time_off.policy_timeline.descriptions.previous_assignments')
    }
  }

  const isLastAssignment =
    timeline?.items[0]?.policy_assignment_id === selectedTimelineItem?.policy_assignment_id

  return (
    <>
      <Title
        variant="500"
        illustration={'group'}
        title={selectedAssignmentTitle()}
        description={
          isLastAssignment
            ? i18n.t('common.from_date', {
                date: formatBackendDateToDayMonthYear(selectedTimelineItem?.start_at ?? '', true),
              })
            : i18n.t('time_off.policy_timeline.item_range', {
                start_on: formatBackendDateToDayMonthYear(
                  selectedTimelineItem?.start_at ?? '',
                  true
                ),
                finish_on: formatBackendDateToDayMonthYear(
                  selectedTimelineItem?.end_at ?? '',
                  true
                ),
              })
        }
      />
      {!employeeCanEditPolicyAssignments && timeline?.items && timeline?.items?.length > 1 && (
        <SelectField
          label=""
          options={policyOptions}
          onChange={(value) => onSelectAssignment(value)}
        />
      )}
      {employeeCanEditPolicyAssignments && (
        <Button onClick={toggleSidePanel(true)} type="secondary">
          {i18n.t('time_off.policy_timeline.list_assignments')}
        </Button>
      )}
      <Sidepanel
        width="wide"
        open={showSidePanel}
        title={i18n.t('time_off.policy_timeline.title')}
        onClose={toggleSidePanel(false)}
        closeLabel={i18n.t('common.close')}
        boxPadding={false}
      >
        <NoticeBox
          key="warning"
          message={
            <Box maxWidth="s400">
              <Text.Normal>{i18n.t('time_off.policy_timeline.warning_message')}</Text.Normal>
            </Box>
          }
          paddingX="s4"
          color="grey400"
          component={<LearnMoreLink />}
        />
        {timeline && (
          <List
            employeeId={employeeId}
            timelineItems={timeline.items}
            currentAssignmentId={timeline.current?.policy_assignment_id}
            selectedTimelineItem={selectedTimelineItem}
            onSelectTimelineItem={onSelectTimelineItem}
            fetchTimeline={fetchTimeline}
          ></List>
        )}
        {showUpsertForm ? (
          <Box paddingTop="s16">
            <UpsertAssignment
              employeeId={employeeId}
              toggleUpsertForm={toggleUpsertForm}
              fetchTimeline={fetchTimeline}
              isDefaultAssignment={false}
            />
          </Box>
        ) : (
          <Box alignItems="flexEnd" paddingTop="s32" paddingRight="s16">
            <Button onClick={toggleUpsertForm(true)} type="secondary" iconName="add">
              {i18n.t('time_off.policy_timeline.create_assignment.title')}
            </Button>
          </Box>
        )}
      </Sidepanel>
    </>
  )
}

export default observer(PolicyAssignments)
